// byClaude-start（移除静态 import，改由父组件懒加载后传入）
// import sayHiImg from './images/sayhi.gif';
// byClaude-end

// 移除埋点依赖，保留原有点击行为

import '@vcredit/fe-lib/CapiChatDialog.css';
import classNames from 'classnames';
import { t } from 'i18next';
import { omit } from 'lodash-es';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNonPassiveEventListener } from '../../../hooks';
import useSafeBarHeight from '../../../hooks/useStatusBarHeight';
import { handleEnClsName } from '../../../utils/util';

import style from './index.module.less';
// bycursor-start
import useCapiIconAnimation from './hooks/useCapiIconAnimation';
// bycursor-end

/**
 * @description CapiAppIcon draggable with bottom overlay image
 * @author cursor
 */
/**
 * @description CapiAppIcon component
 * @author cursor
 */
enum IconHalfOcclusionType {
  None = 'none',
  Left = 'left',
  Right = 'right',
}

function CapiAppIcon(props: {
  prop: React.ImgHTMLAttributes<HTMLImageElement> | boolean | undefined;
  ApiShowMsg?: string;
  onClick: () => void;
  /** byClaude: 由父组件懒加载后传入的 gif 地址（白名单用 v2，否则用默认） */
  gifSrc?: string;
}) {
  const { prop, ApiShowMsg, onClick, gifSrc } = props;

  let bubbleText = ''; // 'Capi幫你貸';
  if (ApiShowMsg) {
    bubbleText = ApiShowMsg;
  }

  const [point, setPoint] = useState<{ x: number; y: number }>();
  const imgRef = useRef<HTMLDivElement | null>(null);
  const [halfIconSize, setHalfIconSize] = useState(0);
  const [moving, setMoving] = useState(false);
  const [dockRight, setDockRight] = useState(true);
  // bycursor-start
  // 使用动画控制 Hook
  const animation = useCapiIconAnimation({
    bubbleText,
    moving,
    autoStart: true,
  });
  // bycursor-end

  const [iconSize, setIconSize] = useState(0);
  const [viewportSize, setViewportSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const [safeBarHeight] = useSafeBarHeight();
  const [safeBottomHeight] = useSafeBarHeight();

  const safeBorder = useMemo(() => {
    const safeBottomLimit = Math.max(0, viewportSize.height - safeBottomHeight);
    const iconBottomLimit = Math.max(0, viewportSize.height - iconSize);
    return {
      top: safeBarHeight,
      left: 0,
      right: Math.max(0, viewportSize.width - iconSize),
      bottom: Math.min(safeBottomLimit, iconBottomLimit),
    };
  }, [iconSize, safeBarHeight, safeBottomHeight, viewportSize.height, viewportSize.width]);

  const onTouchMove = (event: TouchEvent) => {
    setMoving(true);
    // bycursor-start
    // 拖拽时重置所有动画
    animation.resetAllAnimations();
    // bycursor-end
    setPoint({
      x: event.touches[0].pageX - halfIconSize,
      y: event.touches[0].pageY - halfIconSize,
    });
    event.preventDefault();
    event.stopPropagation();
  };

  const onTouchEnd = () => {
    if (!moving) {
      return;
    }
    setTimeout(() => {
      setMoving(false);
    }, 300);
    let newX = point?.x || 0;
    let newY = point?.y || 0;
    if (newX < safeBorder.left) {
      newX = safeBorder.left;
    }
    if (newX > safeBorder.right) {
      newX = safeBorder.right;
    }
    if (newY < safeBorder.top) {
      newY = safeBorder.top;
    }
    if (newY > safeBorder.bottom) {
      newY = safeBorder.bottom;
    }

    const winHalfWidth = window.innerWidth / 2;
    if (newX + halfIconSize > winHalfWidth) {
      newX = safeBorder.right;
      setDockRight(true);
    } else {
      newX = safeBorder.left;
      setDockRight(false);
    }

    setPoint({
      x: newX || 0,
      y: newY || 0,
    });
  };

  useEffect(() => {
    const updateLayoutSize = () => {
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      if (!imgRef.current) {
        return;
      }
      const computedStyle = window.getComputedStyle(imgRef.current);
      const nextIconSize = Number.parseFloat(computedStyle.width) || 0;
      setHalfIconSize(nextIconSize / 2);
      setIconSize(nextIconSize);
    };
    updateLayoutSize();
    window.addEventListener('resize', updateLayoutSize);
    return () => {
      window.removeEventListener('resize', updateLayoutSize);
    };
  }, []);

  // 使用自定义 Hook 替换原来的事件监听器代码
  useNonPassiveEventListener(imgRef, [
    {
      type: 'touchmove',
      handler: onTouchMove as EventListener,
    },
    {
      type: 'touchend',
      handler: onTouchEnd,
    },
  ]);

  const onOpenCapiDialog = () => {
    if (!moving) {
      // bycursor-start
      // 点击时重置所有动画并重新启动
      animation.resetAllAnimations();
      animation.startBubbleAnimation();
      animation.startIconAnimation();
      // bycursor-end
      onClick();
    }
  };

  const iconHalfOcclusionType = useMemo<IconHalfOcclusionType>(() => {
    if (!animation.iconHalfHidden) {
      return IconHalfOcclusionType.None;
    }
    return point?.x === 0 ? IconHalfOcclusionType.Left : IconHalfOcclusionType.Right;
  }, [animation.iconHalfHidden, point?.x]);

  return (
    // 解决小范围移动依旧触发点击事件的问题
    <div onClick={onOpenCapiDialog}>
      <div
        ref={imgRef}
        className={classNames(style.whatsapp, {
          [style.iconHalfHiddenLeft]: iconHalfOcclusionType === IconHalfOcclusionType.Left,
          [style.iconHalfHiddenRight]: iconHalfOcclusionType === IconHalfOcclusionType.Right,
        })}
        style={{
          top: point?.y,
          left: point?.x,
        }}
      >
        {bubbleText ? (
          <div
            className={`${handleEnClsName(style.bubble)} ${handleEnClsName(style.bubbleImage)} ${
              dockRight ? style.bubbleRight : style.bubbleLeft
            } ${animation.bubbleVisible ? style.bubbleFadeIn : style.bubbleFadeOut}`}
            style={{
              opacity: animation.bubbleVisible ? undefined : 0,
            }}
          >
            <div className={` ${dockRight ? style.spanbubbleRight : style.spanbubbleLeft}`}>
              {bubbleText}
            </div>
          </div>
        ) : null}
        <img alt="" src={gifSrc} className={style.mainImg} {...omit(prop, 'className')} />
        <div
          className={classNames(handleEnClsName(style.overlayImg), {
            [style.overlayImgAlignRight]: iconHalfOcclusionType === IconHalfOcclusionType.Left,
            [style.overlayImgAlignLeft]: iconHalfOcclusionType === IconHalfOcclusionType.Right,
          })}
        >
          {iconHalfOcclusionType === IconHalfOcclusionType.None
            ? t('capi_need_help')
            : t('capi_need_help_half_hidden')}
        </div>
      </div>
    </div>
  );
}
// cursor-en

export default CapiAppIcon;
// Contains cursor-generated edits.
// Contains claude-generated edits.
